import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { RevealText, Reveal } from "@/components/RevealText";
import { getService } from "@/lib/api";

export default function ServiceDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setS(null);
    setError(false);
    getService(id).then(setS).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="pt-44 pb-40 max-w-3xl mx-auto px-6 text-center">
        <h1 className="font-display text-5xl uppercase tracking-tighter mb-6">Not found</h1>
        <Link to="/" className="font-mono text-xs tracking-[0.2em] uppercase border border-white/20 px-6 py-4 hover:bg-white hover:text-black transition-colors">Back home</Link>
      </div>
    );
  }
  if (!s) return <div className="pt-44 pb-40 max-w-3xl mx-auto px-6 font-mono text-white/40">Loading…</div>;

  return (
    <div className="pt-32 md:pt-44" data-testid="service-detail-page">
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-20 border-b border-white/10">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/40 mb-8 flex items-center gap-3">
          <span className="w-8 h-px bg-[color:var(--accent)]" /> Service {s.index}
        </p>
        <h1 className="font-display text-6xl md:text-[8.5vw] uppercase tracking-tighter leading-[0.82]">
          <RevealText lines={[s.title]} />
        </h1>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mt-8">
          <p className="font-mono text-base md:text-lg text-white/60 max-w-xl">{s.description}</p>
          <Link to="/contact" className="group inline-flex items-center gap-3 bg-white text-black px-8 py-5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--accent)] hover:text-white transition-colors whitespace-nowrap self-start" data-testid="service-cta">
            Start a project <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Cover + outcomes */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="aspect-[16/10] border border-white/10 overflow-hidden">
            <img src={s.cover} alt={s.title} className="w-full h-full object-cover grayscale" />
          </div>
        </div>
        <div className="lg:col-span-5">
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-6">What you get</p>
          <ul className="space-y-5">
            {s.outcomes.map((o) => (
              <li key={o} className="flex items-start gap-4 font-mono text-base text-white/75">
                <Check size={18} className="text-[color:var(--accent)] mt-1 shrink-0" /> {o}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section className="border-y border-white/10 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-32">
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.85] mb-16">How we work</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-white/10">
            {s.process.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="border-r border-b border-white/10 p-8 md:p-10">
                <span className="font-mono text-xs tracking-[0.25em] text-[color:var(--accent)] mb-6 block">{p.n}</span>
                <h3 className="font-display text-2xl uppercase tracking-tight mb-3">{p.t}</h3>
                <p className="font-mono text-sm text-white/50 leading-relaxed">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-32">
        <div className="mb-14">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/40 mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[color:var(--accent)]" /> Pricing
          </p>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.85]">Simple, honest tiers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {s.pricing.map((p) => (
            <Reveal key={p.name}>
              <div className={`border p-8 md:p-10 h-full flex flex-col ${p.featured ? "border-[color:var(--accent)] bg-[color:var(--surface)]" : "border-white/10"}`} data-testid={`pricing-${p.name}`}>
                {p.featured && <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--accent)] mb-4">Most popular</span>}
                <h3 className="font-display text-2xl uppercase tracking-tight mb-2">{p.name}</h3>
                <p className="font-mono text-sm text-white/50 mb-6">{p.desc}</p>
                <div className="font-display text-4xl tracking-tighter mb-8">{p.price}</div>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 font-mono text-sm text-white/70">
                      <Check size={16} className="text-[color:var(--accent)] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className={`inline-flex items-center justify-center gap-2 px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase transition-colors ${p.featured ? "bg-[color:var(--accent)] text-white hover:bg-white hover:text-black" : "border border-white/20 hover:bg-white hover:text-black"}`}>
                  Get started
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="font-mono text-xs text-white/40 mt-6">* Indicative pricing. Every engagement is scoped to your needs.</p>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-32">
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.85] mb-12">FAQ</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="service-faq">
            {s.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10">
                <AccordionTrigger className="font-display text-xl md:text-2xl uppercase tracking-tight text-left hover:no-underline py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="font-mono text-sm text-white/55 leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
